import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn, Avatar, AvatarImage, AvatarFallback } from "@tellescope/ui";
import { formatInitials } from "@tellescope/utils";
import { useVideoCalling } from "../hooks/useVideoCallingContext";
import { useTrackAttachment } from "../hooks/useTrackAttachment";
import type { LocalUserInfo } from "../types";

export interface LocalCameraTileProps {
  /** Local user info (for avatar and initials when the camera is off). */
  localUser?: LocalUserInfo;
  /** Avatar size shown when the camera is off: "lg" when the tile fills the view, "sm" for PIP/filmstrip. */
  avatarSize?: "sm" | "lg";
  /** Additional class name. */
  className?: string;
}

/**
 * LocalCameraTile - Renders the local camera feed (mirrored), with an avatar fallback when the
 * camera is off. Owns its own video element and attach lifecycle so it can be placed in different
 * layouts (full view, picture-in-picture, or presentation filmstrip) without losing its track.
 */
export const LocalCameraTile = ({ localUser, avatarSize = "lg", className }: LocalCameraTileProps) => {
  const { t } = useTranslation("videoCalling");
  const videoCalling = useVideoCalling();

  const { localVideoTrack, isVideoEnabled } = videoCalling;
  const videoRef = useTrackAttachment<HTMLVideoElement>(localVideoTrack);

  const initials = useMemo(() => {
    if (localUser?.fname || localUser?.lname) {
      return formatInitials(localUser);
    }
    return t("You");
  }, [localUser, t]);

  return (
    <div className={cn("relative w-full h-full overflow-hidden bg-gray-800", className)}>
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className={cn("w-full h-full object-cover scale-x-[-1]", !isVideoEnabled && "hidden")}
      />

      {!isVideoEnabled && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
          {avatarSize === "lg" ? (
            <div className="relative w-1/2 max-w-[200px] aspect-square">
              <Avatar className="w-full h-full shadow-lg">
                {localUser?.avatar && <AvatarImage src={localUser.avatar} alt={initials} />}
                <AvatarFallback className="bg-gray-200 text-gray-700 text-[4rem] font-medium">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="absolute top-1 right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-800" />
            </div>
          ) : (
            <div className="relative">
              <Avatar className="w-12 h-12 shadow">
                {localUser?.avatar && <AvatarImage src={localUser.avatar} alt={initials} />}
                <AvatarFallback className="bg-gray-200 text-gray-700 text-xl font-medium">{initials}</AvatarFallback>
              </Avatar>
              <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border border-gray-800" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
